import { Injectable } from '@angular/core';
import { isNullish } from '@commonHelpers/math-utils';
import { RequestService } from '@commonServices/request.service';
import { Client as AnalyticsApi, CatalogAdminSessionsCountAveragesDto, CatalogAdminSessionsCountDto, CatalogAdminSessionsCountPerDateDailyDto, CatalogAdminSessionsCountPerDateMonthlyDto, CatalogAdminSessionsCountPerDateWeeklyDto, CatalogAdminSessionsCountPerProfessionGroupDto, DatePickerRange, SessionBenchmarkDto, SessionsPerProfessionGroupBenchmarkDto } from '@interfaces/HttpClient/AnalyticsApiModels';
import { firstValueFrom } from 'rxjs';
import { DaterangeService } from './daterange.service';

@Injectable()
export class CatalogDashboardSessionService {

	constructor(
		private analyticsApi: AnalyticsApi,
		private requestService: RequestService,
		private daterangeService: DaterangeService) { }



	async getSessionsOfCatalogAsync(catalogSourceId: number, dateRange?: DatePickerRange): Promise<CatalogAdminSessionsCountDto> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalog(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalog(catalogSourceId, range.rangeStart, range.rangeEnd));
			}

		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsCountBenchmarkAsync(catalogSourceId: number, dateRange: DatePickerRange): Promise<SessionBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountBenchmark(catalogSourceId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogBenchmarkAsync(catalogSourceId: number, dateRange: DatePickerRange): Promise<SessionBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountCategoryBenchmarkOfCatalog(catalogSourceId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogGroupBenchmarkAsync(catalogSourceId: number, dateRange: DatePickerRange): Promise<SessionBenchmarkDto> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountCategoryGroupBenchmarkOfCatalog(catalogSourceId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsAveragesAsync(catalogSourceId: number): Promise<CatalogAdminSessionsCountAveragesDto> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsAverages(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogPerDateDailyAsync(catalogSourceId: number): Promise<CatalogAdminSessionsCountPerDateDailyDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalogPerDateDaily(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogPerDateWeeklyAsync(catalogSourceId: number): Promise<CatalogAdminSessionsCountPerDateWeeklyDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalogPerDateWeekly(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogPerDateMonthlyAsync(catalogSourceId: number): Promise<CatalogAdminSessionsCountPerDateMonthlyDto[]> {
		try {
			return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalogPerDateMonthly(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsOfCatalogPerProfessionGroupAsync(catalogSourceId: number, dateRange?: DatePickerRange): Promise<CatalogAdminSessionsCountPerProfessionGroupDto[]> {
		try {
			if (isNullish(dateRange)) {
				return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalogPerProfessionGroup(catalogSourceId, (await this.daterangeService.rangeInfo)?.rangeStart, (await this.daterangeService.rangeInfo)?.rangeEnd));
			} else {
				const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
				return firstValueFrom(this.analyticsApi.catalogAdminSession_GetSessionsOfCatalogPerProfessionGroup(catalogSourceId, range.rangeStart, range.rangeEnd));
			}
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getSessionsCountPerProfessionGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<SessionsPerProfessionGroupBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountPerProfessionGroupBenchmark(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getSessionsCountPerProfessionGroupCategoryBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<SessionsPerProfessionGroupBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountPerProfessionGroupCategoryBenchmarkOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
	async getSessionsCountPerProfessionGroupCategoryGroupBenchmarkAsync(catalogId: number, dateRange: DatePickerRange): Promise<SessionsPerProfessionGroupBenchmarkDto[]> {
		try {
			const range = await this.daterangeService.getDatepickerSelectionByRange(dateRange);
			return firstValueFrom(this.analyticsApi.catalogAdminSessionBenchmark_GetSessionsCountPerProfessionGroupCategoryGroupBenchmarkOfCatalog(catalogId, range.rangeStart, range.rangeEnd));
		}
		catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}
}
